import React, {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";

import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
  useQuery,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GoogleMap, LoadScript } from "@react-google-maps/api";
import { useClassName } from "@travpro/hooks";
import i18n from "i18n-react";
import { Multiselect } from "multiselect-react-dropdown";
import SimpleBarReact from "simplebar-react";

import logo from "assets/images/logo/logo_white.png";

import BackButton from "components/BackButton";
import DirectoryFilters from "components/DirectoryFilters/DirectoryFilters";
import DirectoryPagination from "components/DirectoryPagination";
import DirectoryProductPopup from "components/DirectoryProductPopup";
import GetDirectoryProducts from "components/GetDirectoryProducts";
import GetMapMarkers from "components/GetMapMarkers";

import APIHandler from "config/APIHandler";

import "simplebar/src/simplebar.css";

import {
  checkboxOptions,
  cityOptions,
  productTypeOptions,
  seasonOptions,
  tagOptions,
  targetOptions,
} from "./filterOptions";
import { generateQuery } from "./GraphQLQuerys";

let containerStyle = {
  width: "100%",
  height: "calc(100vh - 8rem)",
};

if (new URLSearchParams(window.location.search).get("mobile")) {
  containerStyle = {
    width: "100%",
    height: "100vh",
  };
}

// Setting up GraphQL call
const getGQL = (token) => {
  const httpLink = createHttpLink({
    uri: "https://cms.travpromobile.com/api/directory-graphql",
  });

  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      authorization: `Bearer ${token}`,
    },
  }));

  return authLink.concat(httpLink);
};

const getToken = async (callback) => {
  const response = await APIHandler.getDirectoryToken();
  callback(response.access_token);
};

const reducer = (output, [key, value]) => ({
  ...output,
  ...(value !== undefined ? { [key]: value } : {}),
});

const defaultFilters = {
  offset: 0,
  limit: 50,
  orderBy: "asc",
};

const DirectoryView = () => {
  const [popupActive, setPopupActive] = useState(false);
  const [productPopupData, setproductPopupData] = useState(null);
  const [filterData, setFilterData] = useState(defaultFilters);
  const [mapData] = useState({
    zoom: 5,
    center: {
      lat: 64.9146659,
      lng: 26.0672554,
    },
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [source, setSource] = useState(false);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("mobile")) {
      setMobile(JSON.parse(params.get("mobile")));
    }
    if (params.get("source")) {
      setSource(JSON.parse(params.get("source")));
    }
  }, []);

  const variables = useMemo(
    () => Object.entries(filterData).reduce(reducer, {}),
    [filterData]
  );
  const query = useMemo(() => generateQuery(variables), [variables]);

  const data = useQuery(query, { variables });

  const map = useRef();

  const togglePopup = useCallback(({ id }) => {
    setPopupActive((prev) => !prev);
    setproductPopupData((prev) => (!prev ? id : null));
  }, []);

  const toggleFilter = useCallback(() => {
    setFilterOpen((prev) => !prev);
  }, []);

  const switchPage = useCallback(
    (direction) => {
      setFilterData((prev) => ({
        ...prev,
        offset:
          filterData.offset +
          (direction === "prev" ? -filterData.limit : filterData.limit),
      }));
    },
    [filterData.limit, filterData.offset]
  );
  const params = new URLSearchParams(window.location.search);

  return (
    <div className="directoryContainer">
      {source && (
        <style>{".cookieConsent { display: none !important; } "}</style>
      )}
      {mobile && (
        <style>
          {
            "\
        .cookieConsent {display: none !important; }.directoryContainer { height: 100vh; } .results { top: 0 !important; } .listContainer { height: calc(100vh - 8rem) !important; }"
          }
        </style>
      )}
      {!mobile && (
        <div className="header">
          {!source && (
            <BackButton
              useHistory={false}
              text={i18n.translate("general.back")}
            />
          )}
          <img src={`/${logo}`} className="logo" alt="logo" />
        </div>
      )}

      {popupActive && (
        <DirectoryProductPopup
          togglePopup={togglePopup}
          product={productPopupData}
        />
      )}

      <div className="directoryView">
        <div className="leftSide">
          <div className="results">
            <button type="button" onClick={toggleFilter} className="filter-btn">
              {filterOpen ? (
                <FontAwesomeIcon icon={["fas", "times"]} />
              ) : (
                <FontAwesomeIcon icon={["fas", "filter"]} />
              )}
            </button>

            <DirectoryPagination
              result={data}
              offset={filterData.offset}
              limit={filterData.limit}
              switchPage={switchPage}
              filterOpen={filterOpen}
            />
          </div>

          <div className="leftGrid">
            <div className={`filterContainer ${filterOpen ? "active" : ""}`}>
              <DirectoryFilters
                defaultFilters={defaultFilters}
                onChange={setFilterData}
                data={[
                  { id: "search", type: "search" },
                  { id: "cities", options: cityOptions },
                  { id: "type", options: productTypeOptions },
                  { id: "tags", options: tagOptions },
                  { id: "months", options: seasonOptions },
                  { id: "targetGroups", options: targetOptions },
                  { id: "checks", type: "check", options: checkboxOptions },
                ]}
              />
            </div>

            <SimpleBarReact
              className={`listContainer ${filterOpen ? "filterOpen" : ""}`}
            >
              <GetDirectoryProducts togglePopup={togglePopup} result={data} />
            </SimpleBarReact>
          </div>
        </div>
        <div className="rightSide">
          <LoadScript googleMapsApiKey="AIzaSyBpfyrBMTrgHH3YnaGHxfjEN_w7OXBJoqc">
            <GoogleMap
              mapContainerStyle={containerStyle}
              {...mapData}
              onLoad={(mapRef) => {
                map.current = mapRef;
              }}
            >
              <GetMapMarkers togglePopup={togglePopup} result={data} />
            </GoogleMap>
          </LoadScript>
        </div>
      </div>
    </div>
  );
};

const DirectoryViewWithApollo = () => {
  const [client, setClient] = useState();
  const [token, setToken] = useState();

  useEffect(() => {
    getToken(setToken);
  }, []);

  useEffect(() => {
    if (client === undefined && token !== undefined) {
      const newClient = new ApolloClient({
        link: getGQL(token),
        cache: new InMemoryCache(),
      });
      setClient(newClient);
    }
  }, [token, client]);

  return (
    client !== undefined && (
      <ApolloProvider client={client}>
        <DirectoryView />
      </ApolloProvider>
    )
  );
};

export default DirectoryViewWithApollo;

export class OldDirectoryView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      coordinates: {
        lat1: 70.3383179203988,
        lat2: 58.11790267069038,
        lon1: 39.56725593644182,
        lon2: 12.567254863558212,
      },
      width: null,
      mobile: false,
      zoom: 5,
      center: {
        lat: 64.9146659,
        lng: 26.0672554,
      },
      filterOpen: false,
      popupActive: false,
      productPopupData: null,
      filterData: {},
      limit: 50,
      offset: 0,
      link: undefined,
    };

    this.setOffset = this.setOffset.bind(this);
    this.switchPage = this.switchPage.bind(this);
    this.toggleFilter = this.toggleFilter.bind(this);
    this.togglePopup = this.togglePopup.bind(this);
    this.updateFilterData = this.updateFilterData.bind(this);
  }

  componentDidMount() {
    this.getDirectoryToken();
  }

  setOffset(offset) {
    this.setState({ offset });
  }

  async getDirectoryToken() {
    const response = await APIHandler.getDirectoryToken();

    this.setState({ link: getGQL(response.access_token) });
  }

  updateFilterData(filterData) {
    this.setState({ filterData });
  }

  togglePopup(product) {
    this.setState((prev) => ({
      popupActive: !prev.popupActive,
      productPopupData: !prev.productPopupData ? product.id : null,
    }));
  }

  loadProperties(bounds, zoom) {
    this.setState({
      lat1: bounds.getNorthEast().lat(),
      lat2: bounds.getSouthWest().lat(),
      lon1: bounds.getNorthEast().lng(),
      lon2: bounds.getSouthWest().lng(),
      zoom,
    });
  }

  flyToMarker(lat, lon) {
    this.setState({
      zoom: 15,
      center: {
        lat: Number(lat),
        lng: Number(lon),
      },
    });
  }

  resetMap() {
    this.setState({
      zoom: 6,
      center: {
        lat: 24.372665,
        lng: -77.299523,
      },
    });
  }

  toggleFilter() {
    this.setState((prev) => ({
      filterOpen: !prev.filterOpen,
    }));
  }

  switchPage(direction) {
    const { offset, limit } = this.state;
    if (direction === "prev") {
      this.setState({
        offset: offset - limit,
      });
    } else {
      this.setState({
        offset: offset + limit,
      });
    }
  }

  render() {
    const {
      zoom,
      center,
      filterOpen,
      popupActive,
      productPopupData,
      filterData,
      limit,
      offset,
      link,
    } = this.state;

    const client = link
      ? new ApolloClient({ link, cache: new InMemoryCache() })
      : false;

    const params = new URLSearchParams(window.location.search);
    let mobile = false;
    if (params.get("mobile")) {
      mobile = true;
    }
    console.log(mobile);

    return (
      client && (
        <ApolloProvider client={client}>
          <div className="directoryContainer">
            {popupActive && (
              <DirectoryProductPopup
                togglePopup={this.togglePopup}
                product={productPopupData}
              />
            )}

            <div className="directoryView">
              <div className="leftSide">
                <div className="results">
                  <button
                    type="button"
                    onClick={this.toggleFilter}
                    className="filter-btn"
                  >
                    {filterOpen ? (
                      <FontAwesomeIcon icon={["fas", "times"]} />
                    ) : (
                      <FontAwesomeIcon icon={["fas", "filter"]} />
                    )}
                  </button>
                  <DirectoryPagination
                    offset={offset}
                    limit={limit}
                    switchPage={this.switchPage}
                    setOffset={this.setOffset}
                    filterOpen={filterOpen}
                  />
                </div>

                <div className="leftGrid">
                  <div
                    className={`filterContainer ${filterOpen ? "active" : ""}`}
                  >
                    <DirectoryFilters
                      onChange={this.updateFilterData}
                      data={[
                        { id: "search", type: "search" },
                        { id: "cities", options: cityOptions },
                        { id: "type", options: productTypeOptions },
                        { id: "tags", options: tagOptions },
                        { id: "months", options: seasonOptions },
                        { id: "targetGroups", options: targetOptions },
                        {
                          id: "checks",
                          type: "check",
                          options: ["accessible", "certificates"],
                        },
                      ]}
                    />
                  </div>

                  <SimpleBarReact
                    className={`listContainer ${
                      filterOpen ? "filterOpen" : ""
                    }`}
                  >
                    <GetDirectoryProducts
                      limit={limit}
                      offset={offset}
                      togglePopup={this.togglePopup}
                      {...filterData}
                    />
                  </SimpleBarReact>
                </div>
              </div>
              <div className="rightSide">
                <LoadScript googleMapsApiKey="AIzaSyBpfyrBMTrgHH3YnaGHxfjEN_w7OXBJoqc">
                  <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={center}
                    zoom={zoom}
                    onLoad={(map) => {
                      this.map = map;
                    }}
                  >
                    <GetMapMarkers
                      limit={limit}
                      offset={offset}
                      togglePopup={this.togglePopup}
                      {...filterData}
                    />
                  </GoogleMap>
                </LoadScript>
              </div>
            </div>
          </div>
        </ApolloProvider>
      )
    );
  }
}
